import styles from "./AdjustmentTable.module.scss"

import type { FragmentType } from "@/company_admin/gql/__generated__"
import { getFragmentData, graphql } from "@/company_admin/gql/__generated__"
import { getJstDate, getJstFullYear, getJstMonth } from "@/utils/jst_date"

export const adjustmentFragment = graphql(/* GraphQL */ `
	fragment AdjustmentFields on Adjustment {
		id
		amount {
			amount
			isPayout
		}
		name
		memo
		registerTime
	}
`)

export const accountingAmountFragment = graphql(/* GraphQL */ `
	fragment AccountingAmountFields on AccountingAmount {
		amount
		isPayout
	}
`)

type AdjustmentTableProps = {
	adjustments: readonly FragmentType<typeof adjustmentFragment>[]
	total: FragmentType<typeof accountingAmountFragment>
	isSouhlAdmin: boolean
	onDelete: (adjustmentId: string) => void
	isDeleting: boolean
}

const formatAmount = (amount: number): string => {
	return `¥${amount.toLocaleString("ja-JP")}`
}

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr)
	const y = getJstFullYear(date)
	const m = String(getJstMonth(date) + 1).padStart(2, "0")
	const d = String(getJstDate(date)).padStart(2, "0")
	return `${y}/${m}/${d}`
}

const AdjustmentTable = ({
	adjustments: adjustmentsRaw,
	total: totalRaw,
	isSouhlAdmin,
	onDelete,
	isDeleting,
}: AdjustmentTableProps) => {
	const adjustments = getFragmentData(adjustmentFragment, adjustmentsRaw)
	const total = getFragmentData(accountingAmountFragment, totalRaw)

	if (adjustments.length === 0) {
		return <div className={styles.empty}>この年月には調整がありません。</div>
	}

	return (
		<div className={styles.table_wrapper}>
			<table className={styles.table}>
				<thead>
					<tr>
						<th className={styles.th}>金額</th>
						<th className={styles.th}>支払い種別</th>
						<th className={styles.th}>調整名</th>
						<th className={styles.th}>メモ</th>
						<th className={styles.th}>作成日</th>
						{isSouhlAdmin && <th className={styles.th_action}>削除</th>}
					</tr>
				</thead>
				<tbody>
					{adjustments.map((adj) => (
						<tr key={adj.id} className={styles.tr}>
							<td className={styles.td}>{formatAmount(adj.amount.amount)}</td>
							<td className={styles.td}>
								{adj.amount.isPayout ? "SOUHL 振込" : "御社 支払い"}
							</td>
							<td className={styles.td}>{adj.name}</td>
							<td className={styles.td}>{adj.memo ?? "-"}</td>
							<td className={styles.td}>{formatDate(adj.registerTime)}</td>
							{isSouhlAdmin && (
								<td className={styles.td_action}>
									<button
										className={styles.delete_button}
										onClick={() => onDelete(adj.id)}
										disabled={isDeleting}
										type="button"
										title="削除"
									>
										🗑
									</button>
								</td>
							)}
						</tr>
					))}
				</tbody>
				<tfoot>
					<tr className={styles.total_row}>
						<td className={styles.td_total} colSpan={2}>
							合計: {formatAmount(total.amount)}（
							{total.isPayout ? "SOUHL 振込" : "御社 支払い"}）
						</td>
						<td colSpan={isSouhlAdmin ? 4 : 3} className={styles.td} />
					</tr>
				</tfoot>
			</table>
		</div>
	)
}

export default AdjustmentTable
